import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getClientList } from '../services/clientService';
import { buildStorageUrl } from '../api/api';

// const IMAGE_BASE_URL = "http://schoolcabservice.test/storage/clientSection/"; // Adjust base URL as needed

export const ClientsSection = () => {
    const [clients, setClients] = useState([]);
    const [sectionTitle] = useState("Our Clients");
    const [hasFired, setHasFired] = useState(false);
    const sectionRef = useRef(null);
    const location = useLocation();

    // Trigger fetch if visible
    useEffect(() => {
        if (hasFired) return;

        // On normal home load (no hash), fetch immediately instead of waiting for scroll.
        if (!location.hash) {
            setHasFired(true);
            return;
        }

        const targetId = 'our-partners';

        // 1. Immediate match for target hash
        if (location.hash === `#${targetId}`) {
            setHasFired(true);
            return;
        }

        let observer = null;
        let timer = null;

        const startObserver = () => {
            observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setHasFired(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1, rootMargin: '0px' }
            );
            if (sectionRef.current) observer.observe(sectionRef.current);
        };

        // 2. Logic: If there is a hash for SOME OTHER section, we delay
        if (location.hash && location.hash !== `#${targetId}`) {
            timer = setTimeout(startObserver, 2000);
        } else {
            startObserver();
        }

        return () => {
            if (timer) clearTimeout(timer);
            if (observer) observer.disconnect();
        };
    }, [hasFired, location.hash]);

    // fetch data
    useEffect(() => {
        if (!hasFired) return;

        const loadData = async () => {
            try {
                const clientsList = await getClientList();
                console.log("All Clients Fetched:", clientsList);
                setClients(clientsList);
            } catch (err) {
                console.log("API ERROR:", err);
            }
        };

        loadData();
    }, [hasFired]);

    // owl carousel control
    useEffect(() => {
        if (!window.$ || clients.length === 0) return;

        const initSlider = () => {
            const slider = window.$("#partners-slider");

            if (slider.hasClass("owl-loaded")) {
                slider.trigger("destroy.owl.carousel");
                slider.removeClass("owl-loaded");
                slider.find(".owl-stage-outer").children().unwrap();
            }

            slider.owlCarousel({
                items: 5, // Reduced from 5 to give more space
                margin: 30, // Added margin for spacing
                loop: clients.length > 5, 
                autoplay: true,
                autoplayTimeout: 3000,
                smartSpeed: 600,
                responsive: {
                    0: { items: 1 },
                    480: { items: 2 },
                    768: { items: 3 },
                    992: { items: 5 } // Show 5 items on large screens for better clarity and space
                }
            });
        };

        // IMPORTANT — wait until browser paints layout
        const timer = setTimeout(initSlider, 800);

        // also re-init on window resize (fix width 0 bug)
        window.addEventListener("load", initSlider);
        window.addEventListener("resize", initSlider);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("load", initSlider);
            window.removeEventListener("resize", initSlider);
        };
    }, [clients]);

    return (
        <section id="our-partners" className="padding" ref={sectionRef} style={{ paddingTop: '40px', paddingBottom: '40px', minHeight: clients.length > 0 ? 'auto' : '500px' }}>
            {clients.length > 0 ? (
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-3 col-md-3 col-sm-12 pr-lg-5">
                            <div className="text-center text-md-left wow fadeIn" data-wow-delay="300ms">
                                <h2 className="heading bottom30 darkcolor pt-1">
                                    <span className="d-block">{sectionTitle}</span>
                                </h2>
                            </div>
                        </div>

                        <div className="col-lg-9 col-md-9 col-sm-12 pl-lg-3">
                            <div id="partners-slider" className="owl-carousel">
                                {clients.map((client) => {
                                    const imageUrl = buildStorageUrl('clientSection', client.image);
                                    
                                    return (
                                        <div className="item" key={client.id}>
                                            <div className="logo-item text-center">
                                                <img 
                                                    src={imageUrl} 
                                                    alt={client.name} 
                                                    className="mb-2" 
                                                    style={{ maxHeight: '100px', objectFit: 'contain', margin: '0 auto', width: 'auto', maxWidth: '100%' }} 
                                                    onError={(e) => {
                                                        e.target.onerror = null; 
                                                        e.target.src = "https://via.placeholder.com/150x80?text=No+Image";
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
};



const OurClients = () => {
    return (
        <>
            {/* Page Header */}
            <section id="main-banner-page" className="position-relative page-header contact-header section-nav-smooth parallax" style={{ zIndex: 5 }}>
                <div className="overlay overlay-dark opacity-6"></div>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 offset-lg-3">
                            <div className="page-titles whitecolor text-center padding">
                                <h2 className="font-bold">Our Clients</h2>
                                <h3 className="font-light py-2">Trusted by schools everywhere.</h3>
                            </div>
                        </div>
                    </div>
                    <div className="gradient-bg title-wrap mt-n5">
                        <div className="row">
                            <div className="col-lg-12 col-md-12 whitecolor">
                                <h3 className="float-left">Our Clients</h3>
                                <ul className="breadcrumb top10 bottom10 float-right">
                                    <li className="breadcrumb-item hover-light"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item hover-light">Our Clients</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <ClientsSection />
        </>
    );
};

export default OurClients;

